/**
 * Client mirror of the backend capability catalog
 * (backend/src/auth/rbac/capabilities.ts). Used to render the roles matrix and
 * the per-user capability grid. Keep the strings in sync with the backend — the
 * server is the source of truth for enforcement; this is UX only.
 */
import type { Depth, Persona } from '../types/api';

export const DEPTHS: Depth[] = ['none', 'view', 'manage', 'approve', 'admin'];
export const PERSONAS: Persona[] = ['cfo', 'coo', 'cmo', 'ceo'];

export interface CapDef {
  id: string;
  label: string;
  sensitive?: boolean;
}
export interface CapGroup {
  group: string;
  caps: CapDef[];
}

export const CAP_CATALOG: CapGroup[] = [
  {
    group: 'Conversation & Dashboard',
    caps: [
      { id: 'convo.ask', label: 'Ask questions' },
      { id: 'dashboard.view', label: 'View dashboard' },
      { id: 'dashboard.pin', label: 'Pin charts' },
      { id: 'briefing.view', label: 'View briefing' },
    ],
  },
  {
    group: 'Tickets',
    caps: [
      { id: 'tickets.view', label: 'View' },
      { id: 'tickets.create', label: 'Create' },
      { id: 'tickets.comment', label: 'Comment' },
      { id: 'tickets.status', label: 'Change status' },
      { id: 'tickets.delete', label: 'Delete', sensitive: true },
    ],
  },
  {
    group: 'Integrations',
    caps: [
      { id: 'integrations.dashboard.view', label: 'Dashboard' },
      { id: 'integrations.exceptions.view', label: 'Exceptions view' },
      { id: 'integrations.exceptions.retry', label: 'Exceptions retry' },
      { id: 'integrations.workflows.view', label: 'Workflows view' },
      { id: 'integrations.workflows.toggle_test', label: 'Toggle (Test)' },
      { id: 'integrations.workflows.promote_live', label: 'Promote LIVE', sensitive: true },
      { id: 'integrations.agent.draft', label: 'Agent draft' },
      { id: 'integrations.approvals.view', label: 'Approvals view' },
      { id: 'integrations.approvals.decide', label: 'Approve / decide', sensitive: true },
    ],
  },
  {
    group: 'Administration',
    caps: [
      { id: 'users.manage', label: 'Create users', sensitive: true },
      { id: 'users.edit', label: 'Edit Users', sensitive: true },
      { id: 'roles.manage', label: 'Manage roles', sensitive: true },
      { id: 'audit.view', label: 'View audit' },
      { id: 'system.admin', label: 'System dashboard', sensitive: true },
      { id: 'system.alerts', label: 'Receive system alerts' },
    ],
  },
];

export const ALL_CAPS: string[] = CAP_CATALOG.flatMap((g) => g.caps.map((c) => c.id));

/**
 * Administration-group caps only an owner can grant to another user (mirrors
 * backend ADMIN_GRANT_CAPS). The modal hides this whole group for non-owners.
 */
export const ADMIN_GRANT_CAPS: string[] = [
  'users.manage',
  'users.edit',
  'roles.manage',
  'system.admin',
  'system.alerts',
];

/** Tooltip copy for each executive persona (the per-card access rows). */
export const PERSONA_HELP: Record<Persona, { title: string; desc: string }> = {
  cfo: { title: 'CFO — Finance', desc: 'P&L, cash, forecasting and spend.' },
  coo: { title: 'COO — Operations', desc: 'Tickets, integrations and cross-system workflows.' },
  cmo: { title: 'CMO — Marketing', desc: 'Campaigns, pipeline and growth metrics.' },
  ceo: { title: 'CEO — Executive', desc: 'Cross-area summary and briefings.' },
};

/** Tooltip copy for each per-card access depth. */
export const DEPTH_HELP: Record<Depth, string> = {
  none: 'No access — the card is locked and hidden from this person.',
  view: 'Read-only — open the card, see dashboard & briefing, and ask questions.',
  manage: 'Full use — everything in View plus day-to-day actions. The normal working level.',
  approve: 'Everything in Manage plus authority to approve changes (you can’t approve your own).',
  admin: 'Full control of that area.',
};
